import type { LicenseLimitChecker } from './licenseLimitChecker';

export const cloudTiers = ['A', 'B', 'C'] as const;

export type CloudTier = (typeof cloudTiers)[number];

export type BaseCloudConstructLicense = {
  // A `numeric` column arrives from, and is written back to, the pg driver as a
  // string -- there is no parser for OID 1700 because the SQL type holds values
  // a JS number cannot represent. Keeping the cost a string end to end means it
  // is never rounded through a float on its way to the database.
  //
  // Nullable because adjustCcuCostIfNeeded clears the cost under a non-ccu
  // license; a construct is always minted with a real one.
  ccu: string | null;
  cloudTier: CloudTier;
  isUnlicensed: boolean;
};

// The single source of what a tier costs, see Confluence "Cloud Licensing -
// public documentation".
// A construct is priced by picking its tier, never by passing a cost, so a
// cost/tier mismatch can't be written. Constructs that are cheaper under some
// condition switch tier at the call site instead of overriding the cost.
//
// BEFORE CHANGING A PRICE: snapshots don't store this table, so old snapshots
// would show the new prices next to their discovery-time costs. Store it in
// the snapshot meta first (with a migration backfilling the current prices).
export const cloudTierPricing = {
  A: { ccu: '1', isUnlicensed: false },
  B: { ccu: '0.5', isUnlicensed: false },
  C: { ccu: '0', isUnlicensed: true },
} as const satisfies Record<
  CloudTier,
  Omit<BaseCloudConstructLicense, 'cloudTier'>
>;

export function buildCommonCloudLicenseFields(
  cloudTier: CloudTier,
  licenseChecker: LicenseLimitChecker,
): BaseCloudConstructLicense {
  const construct: BaseCloudConstructLicense = {
    ...cloudTierPricing[cloudTier],
    cloudTier,
  };
  licenseChecker.adjustCcuCostIfNeeded(construct);
  return construct;
}
